import React, {useMemo} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import StudySearchInput, {StudySearchInputProps, StudySearchItem} from 'studies/StudySearchInput';
import FormGroup from 'ui-library/bs/FormGroup';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export interface BaseStorablePaneStudyableState {
  studyIds?: Array<string>;
}

export interface BaseStorablePaneStudyableProps
  // Confusingly, CreateStorableModal renders `<MyStorablePane {...state} {...props} />`,
  // so in most cases we expect to receive the studyIds stored in state via pane props.
  // Omit<BaseStorablePaneStudyableProps, 'studyIds'> can be used in cases where the
  // state is not spread into the props.
  extends BaseStorablePaneStudyableState {
  /* Props to customize BaseStorableStudyableFormGroup */
  studySearchInputProps?: BaseStorablePaneStudyableFormGroupProps['studySearchInputProps'];
}

export interface BaseStorablePaneStudyableFormGroupProps extends BaseStorablePaneStudyableState {
  /** Additional configuration of the StudySearchInput (e.g. to disable edits) */
  studySearchInputProps?: Omit<StudySearchInputProps, 'selectedItems' | 'onChange'>;
  onUpdate: (params: BaseStorablePaneStudyableState) => void;
}

/**
 * Form group that can be rendered within BaseStorablePane for a studyable storable.
 *
 * For example, if a Foo is a studyable storable:
 * - CreateFooPaneProps should extend BaseStorablePaneStudyableProps, which
 *   allows optionally seeding studyIds / configuring UI for StudySearchInput.
 * - CreateFooPaneState should extend BaseStorablePaneStudyableState.
 * - BaseStorablePaneStudyableFormGroup should be rendered as a child of BaseStorablePane,
 *   @example
 *    <BaseStorablePane {...}>
 *      <BaseStorablePaneStudyableFormGroup
 *        studySearchInputProps={this.props.studySearchInputProps}
 *        studyIds={this.props.studyIds}
 *        onUpdate={this.props.onUpdate}
 *      />
 *    </BaseStorablePane>
 */
export default function BaseStorablePaneStudyableFormGroup(
  props: BaseStorablePaneStudyableFormGroupProps
): JSX.Element | null {
  // Reformat the data into the format expected by the StudySearchInput.
  const selectedItems = useMemo(() => props.studyIds?.map((id) => ({id})) ?? [], [props.studyIds]);
  if (!BenchlingConfig.STUDIES_ENABLE) {
    return null;
  }
  return (
    <FormGroup dataTestAttrs={{component: 'BaseStorablePaneStudyableFormGroup', element: 'root'}}>
      <FieldLabel label="Studies" />
      <StudySearchInput<'id'>
        selectedItems={selectedItems}
        onChange={(studySearchItems: Array<Pick<StudySearchItem, 'id'>>) => {
          props.onUpdate({studyIds: studySearchItems.map((item) => item.id)});
        }}
        shouldAllowMultiple={true}
        {...props.studySearchInputProps}
      />
    </FormGroup>
  );
}
